import { useGeckoLocale } from 'features/locale'
import React, { createContext, ReactNode, useContext } from 'react'
import { GeckoMeasurementSystem, GeckoMeasurementUnit } from 'types'

interface UnitSystemContextType {
  measurementSystem: GeckoMeasurementSystem
  setMeasurementSystem: (system: GeckoMeasurementSystem) => void
  getGeometricUnit: () => GeckoMeasurementUnit
  measurementPrecision: number
  getDistanceUnit: () => GeckoMeasurementUnit
  getTemperatureUnit: () => GeckoMeasurementUnit
  /** Whether the current measurement system is metric (mm) vs imperial (in) */
  isMetric: boolean
}

const UnitSystemContext = createContext<UnitSystemContextType | undefined>(
  undefined
)

export const UnitSystemProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { measurementSystem, setMeasurementSystem } = useGeckoLocale()

  // Helper functions to get appropriate unit based on measurement system
  const getGeometricUnit = () =>
    measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Millimeters
      : GeckoMeasurementUnit.Inches

  const measurementPrecision =
    measurementSystem === GeckoMeasurementSystem.Metric ? 1 : 3

  const getDistanceUnit = () =>
    measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Meters
      : GeckoMeasurementUnit.Feet

  const getTemperatureUnit = () =>
    measurementSystem === GeckoMeasurementSystem.Metric
      ? GeckoMeasurementUnit.Celsius
      : GeckoMeasurementUnit.Fahrenheit

  const isMetric = measurementSystem === GeckoMeasurementSystem.Metric

  return (
    <UnitSystemContext.Provider
      value={{
        measurementSystem,
        setMeasurementSystem,
        getGeometricUnit,
        measurementPrecision,
        getDistanceUnit,
        getTemperatureUnit,
        isMetric,
      }}>
      {children}
    </UnitSystemContext.Provider>
  )
}

export const useUnitSystem = (): UnitSystemContextType => {
  const context = useContext(UnitSystemContext)
  if (context === undefined) {
    throw new Error('useUnitSystem must be used within a UnitSystemProvider')
  }
  return context
}
